/**
 * SOLID Review
 * S — Good. One concern, the client's session: who the tab is, how it gets into the game screen and to a seat, the
 *     funnel's steps and the account's — every rule that moves the session, and what a seat taken writes at the
 *     table. The account is a value object; the table is its own aggregate, which the session asks.
 * O — Good. A new session step is one method here; a new fact about the account is one on `Account`.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Good. One method per decision the session takes, the Memento pair and what the tab keeps; a decision calls one.
 * D — Strong. Its value objects, the table aggregate, the server's dice ranking, the refusal envelope and the schema
 *     types. No store, no port.
 */
import { allowed, refuse, REFUSAL, type TDecisionResult } from '../decisions/shared';
import { sameJson } from '../../shared/sameJson';
import { Account } from './Account';
import { EmailAddress } from '../auth/EmailAddress';
import { ClientTable } from '../table/ClientTable';
import { TableRoom, type IEntityRoomSource } from '../table/TableRoom';
import { DiceRanking } from '../dice/DiceRanking';
import type { IPropsClientInput } from '../ports/schemas/ClientChanges';
import type {
  IStateClientSession, IEntityClientPlayer, IEntityClientGuest, IEntityClientResume, IEntityClientStored, TClientScreen,
} from '../ports/schemas/ClientSession';
import type { IEntityClientDiceRank } from '../ports/schemas/DiceRankingMoves';
import type { IEntityTableAck } from '../ports/schemas/TableEvents';
import type {
  ICommandSignUp, ICommandVerifyCode, ICommandEmailCode, IPayloadSignedIn, IPayloadProfile, IPayloadEmail, IPayloadSignUp,
  IPayloadVerifyCode, IPayloadNickname, IPayloadEmailCode,
} from '../ports/schemas/AuthMoves';
import type {
  ICommandSessionStarted, ICommandJoinRouted, ICommandTableJoined, ICommandTableRejoined, ICommandTableLateJoined,
  ICommandTableHosted, ICommandActiveRoomFound, ICommandRestoreJoined, ICommandRoundRestored, ICommandGuestCreated,
  IEntityEntryPlan, IEntityIntent, IEntityJoinRoute, IEntityRoundRead, IEntityColdRoom, IPayloadMe,
  IPayloadSeat, IPayloadHost, IPayloadPlayer, IPayloadGuest, IPayloadCodeLookup, IPayloadRejoinRequest, IPayloadLateJoin,
  IPayloadRoomCodeLookup,
} from '../ports/schemas/SessionMoves';

/**
 * What a session's moves leave the tab to keep or forget — the guest identity, the stored game — and whether they
 * reset the table: the settling's options, as the decisions passed them.
 */
export interface IConfigSessionKeeps {
  readonly guest?: IEntityClientGuest | null;
  readonly resume?: IEntityClientResume | null;
  readonly reset?: boolean;
}

/** The screens' limit on a display name — IdentityModal's guest name and ProfileModal's nickname alike. */
const MAX_NAME_LENGTH = 32;
const CODE_LENGTH = 4;
/** A table has six seats; a seventh player gets "full". */
const MAX_PLAYERS = 6;
const ROOM_WAITING = 'WAITING';
const ROOM_IN_PROGRESS = 'IN_PROGRESS';
const ROOM_FINISHED = 'FINISHED';

// The screens' words, word for word: the welcome screen and the auth screens show them.
const INVALID_EMAIL = 'Please enter a valid email address.';
const NO_NICKNAME = 'Please enter a nickname.';
const NO_CONSENT = 'You must accept the Terms of Service and Privacy Policy to continue.';
const NO_PROFILE = 'Only a signed-in player has a profile.';
/** The login code's refusal when the server gave no words of its own. */
const LOGIN_CODE_FAILED = 'Failed to send code. Please try again.';
/** useIdentityBootstrap's message when a join was waiting on a token the server refused. */
const SESSION_EXPIRED = 'Your session has expired. Please sign in again to join the table.';
/** The server could not be asked: `me` (FE3) or a table code's lookup. */
const UNREACHABLE = 'Could not reach the server. Please try again.';
const TABLE_NOT_FOUND = 'Table not found. Check the code and try again.';
const TABLE_FULL = 'That game is full (6 players). Try again later.';
const JOIN_FAILED = 'Could not join that table. Please try again.';
const REJOIN_FAILED = 'Could not rejoin that table. Please try again.';
/** The auto-host's words for a failure that was not the server's answer (MainMenuPanel's). */
const UNEXPECTED = 'Unexpected error.';

const APPLIED: IEntityTableAck = { applied: true };
const NO_READ: IEntityRoundRead = { read: null };

/**
 * THE CLIENT'S SESSION, AS AN AGGREGATE (frontend-domain-oo P4, 2026-10-26; handbook §28.271). Until P4 the session
 * was a plain record that the session decisions copied with the table into a draft (`decisions/session/
 * sessionDraft.ts`), edited field by field and settled: the account's rules (`auth.ts`, FE3), the way into the game
 * screen (`entry.ts`, FE2), the funnel (`funnel.ts`), taking a seat (`seating.ts`, with `game/SmartJoinRoute.ts`'s
 * route rule) and the dice ranking kept (`diceRanking.ts`, P2). Those rules are the aggregate's now; a decision
 * rebuilds it, calls one method and settles the snapshot — P5 makes those decisions the `Session` and `Seating`
 * services.
 *
 * Memento: `fromSnapshot` / `toSnapshot`, the session decisions' `{ table, session }`. The session holds the TABLE it
 * sits at, the `ClientTable` aggregate: a seat taken writes there (`identifyAs`, `sitAt`, `enterLobby`, `livesFor`),
 * one identity in two views — the table's `localPlayerId` and the session's player are the same store field, and
 * change together (`seatAs`). The account is a value object (`Account`), the typed address another (`EmailAddress`).
 * What the tab keeps across a reload — the guest identity, the stored game — the session records as it decides
 * (`kept`), and the settling stores or forgets it.
 *
 * Every rule is a hook's, a screen's or a panel's guard, moved and not rewritten; the messages are theirs, word for
 * word. A method answers its decision's answer: a refusal, a request to send, or what the Application does next.
 */
export class ClientSession {
  private keeps: IConfigSessionKeeps = {};

  private constructor(
    private readonly session: IStateClientSession,
    private table: ClientTable,
  ) {}

  /** The session and the table it sits at, from a decision's input — copies: the input is never written. */
  static fromSnapshot(input: IPropsClientInput): ClientSession {
    const session: IStateClientSession = { ...input.session };
    return new ClientSession(session, ClientTable.fromSnapshot(input.table));
  }

  toSnapshot(): IPropsClientInput {
    const snapshot: IPropsClientInput = { table: this.table.toSnapshot(), session: { ...this.session } };
    return snapshot;
  }

  /** What the tab is to keep or forget, and whether the table was reset, as this session's moves decided. */
  kept(): IConfigSessionKeeps {
    return this.keeps;
  }

  private get account(): Account {
    return Account.of(this.session.account);
  }

  /** Seat the tab as `player`: the session's player and the table's local id move together. */
  private seatAs(player: IEntityClientPlayer): void {
    this.session.player = player;
    this.table.identifyAs(player.playerId);
  }

  /**
   * A seat was taken: whatever code was pending is spent, and an old join error is irrelevant (AppRoot cleared it
   * when a room appeared).
   */
  private seatedAt(room: TableRoom): void {
    this.table.sitAt(room);
    this.session.pendingRoomCode = null;
    this.session.pendingJoinError = null;
  }

  /** The welcome screen's banner for a join that did not happen; the code that led to it is dropped. */
  private joinFailed(message: string): void {
    this.session.pendingJoinError = message;
    this.session.pendingRoomCode = null;
  }

  private keepGuest(guest: IEntityClientGuest | null): void {
    this.keeps = { ...this.keeps, guest };
  }

  private keepStoredGame(resume: IEntityClientResume | null): void {
    this.keeps = { ...this.keeps, resume };
  }

  // ── The account (FE3): LoginScreen, VerifyCodeScreen, ProfileModal, the welcome screen's logout ──────────────
  // Each checked its fields and wrote the auth store around one REST call. The token is never here: a code accepted
  // is an identity, and `ClientAuthServer` keeps the JWT that came with it.

  /** The email screen's Continue in sign-in mode: an address, normalised. */
  planLoginCode(email: string): TDecisionResult<IPayloadEmail> {
    const address = EmailAddress.parse(email);
    if (address === null) return refuse(INVALID_EMAIL, REFUSAL.INVALID_INPUT);
    const request: IPayloadEmail = { email: address.value };
    return allowed(request);
  }

  /**
   * The login code's answer: on to the code screen with the address — whatever the address is. The server answers the
   * same for one with an account, an unverified one and one with none (Q10, §28.248), so nothing here can tell them
   * apart; a player who is new reaches sign-up by the form's own choice, not by being told. A refusal (a rate limit,
   * a mailer down) is the server's words.
   */
  loginCodeAnswered(email: string, refusal: string | null): TDecisionResult<IEntityTableAck> {
    if (refusal !== null) return refuse(refusal || LOGIN_CODE_FAILED, REFUSAL.SERVER);
    this.session.pendingEmail = email;
    this.session.screen = 'VERIFY_LOGIN';
    return allowed(APPLIED);
  }

  /** The sign-up form's Continue: an address, a nickname and the consent the law requires. */
  planSignUp(command: ICommandSignUp): TDecisionResult<IPayloadSignUp> {
    if (!EmailAddress.isValid(command.email)) return refuse(INVALID_EMAIL, REFUSAL.INVALID_INPUT);
    const displayName = command.displayName.trim();
    if (displayName.length === 0) return refuse(NO_NICKNAME, REFUSAL.INVALID_NAME);
    if (!command.consentAccepted) return refuse(NO_CONSENT, REFUSAL.INVALID_INPUT);
    const request: IPayloadSignUp = {
      email: command.email.trim(),
      displayName,
      referralCode: command.referralCode,
      consentAccepted: true,
    };
    return allowed(request);
  }

  /** Signed up: on to the code screen with the address, to verify it. */
  signedUp(email: string): void {
    this.session.pendingEmail = EmailAddress.normalised(email);
    this.session.screen = 'VERIFY_EMAIL';
  }

  /** The code screen's Verify: four digits, for the address the code was sent to. */
  planVerifyCode(command: ICommandVerifyCode): TDecisionResult<IPayloadVerifyCode> {
    const email = this.session.pendingEmail;
    if (email === null) return refuse('No address is waiting for a code.', REFUSAL.NOT_READY);
    if (command.code.length !== CODE_LENGTH) return refuse('Please enter all 4 digits.', REFUSAL.INVALID_INPUT);
    const request: IPayloadVerifyCode = { purpose: command.purpose, email, code: command.code };
    return allowed(request);
  }

  /**
   * A code accepted (the auth store's `setAuth`): the tab is that registered account, on the game screen — where the
   * entry asks `me` and seats them — and a guest identity it held is forgotten.
   */
  signedIn(identity: IPayloadSignedIn): void {
    this.session.account = Account.registered(identity.playerId, identity.displayName, identity.matchCredits).toSnapshot();
    this.session.screen = 'GAME';
    this.keepGuest(null);
  }

  /** The profile opened and `me` answered: the account's name, address and credits as the server has them. */
  profileLoaded(profile: IPayloadProfile): boolean {
    if (!this.account.isRegistered()) return false;
    this.session.account = this.account.withProfile(profile).toSnapshot();
    return true;
  }

  /** The profile's Save: a nickname, trimmed, one to 32 characters. */
  planNickname(displayName: string): TDecisionResult<IPayloadNickname> {
    if (!this.account.isRegistered()) return refuse(NO_PROFILE, REFUSAL.NOT_READY);
    const trimmed = displayName.trim();
    if (trimmed.length === 0) return refuse(NO_NICKNAME, REFUSAL.INVALID_NAME);
    if (trimmed.length > MAX_NAME_LENGTH) return refuse(`Name must be ${MAX_NAME_LENGTH} characters or fewer.`, REFUSAL.INVALID_NAME);
    const request: IPayloadNickname = { displayName: trimmed };
    return allowed(request);
  }

  /** The new nickname: the account's, and the seated player's — the table shows the name it holds. */
  nicknameChanged(displayName: string): void {
    this.session.account = this.account.renamed(displayName).toSnapshot();
    const player = this.session.player;
    if (player !== null) this.session.player = { ...player, displayName };
  }

  /** The profile's "send code" for a new address: an address, normalised. */
  planEmailChange(email: string): TDecisionResult<IPayloadEmail> {
    if (!this.account.isRegistered()) return refuse(NO_PROFILE, REFUSAL.NOT_READY);
    const address = EmailAddress.parse(email);
    if (address === null) return refuse(INVALID_EMAIL, REFUSAL.INVALID_INPUT);
    const request: IPayloadEmail = { email: address.value };
    return allowed(request);
  }

  /** The profile's Verify for a new address: four digits. */
  planVerifyEmailChange(command: ICommandEmailCode): TDecisionResult<IPayloadEmailCode> {
    if (!this.account.isRegistered()) return refuse(NO_PROFILE, REFUSAL.NOT_READY);
    const code = command.code.trim();
    if (code.length !== CODE_LENGTH) return refuse('Enter the 4-digit code.', REFUSAL.INVALID_INPUT);
    const request: IPayloadEmailCode = { email: EmailAddress.normalised(command.email), code };
    return allowed(request);
  }

  /** The new address, as the server confirmed it. */
  emailChanged(email: string | null): void {
    this.session.account = this.account.withEmail(email).toSnapshot();
  }

  /**
   * Signing out — the auth store's `clearAuth` and `clearGuest`, one decision for both: the table is reset (the
   * composition root's reset handler), nobody is signed in, the welcome screen. The guest identity is forgotten,
   * and — a FIX, §28.199's finding — so is the stored game: the next player in this tab must not find another
   * player's record, which the entry would trust without asking. A pending join and its error are left as they
   * were, so the expiry message a refused token leaves stays on the welcome screen. The reset clears the entry's,
   * the restore's and the auto-host's flags with the rest of the game store, and the session says so.
   */
  signOut(): void {
    this.table = ClientTable.empty();
    this.session.account = Account.anonymous(0).toSnapshot();
    this.session.player = null;
    this.session.screen = 'WELCOME';
    this.session.pendingEmail = null;
    this.session.hostOnEntry = false;
    this.session.restoring = false;
    this.session.entering = false;
    this.session.hosting = false;
    this.session.hostError = null;
    this.keeps = { reset: true, guest: null, resume: null };
  }

  // ── Getting into the game (FE2): identity bootstrap, session recovery, cold rejoin, smart-join routing ───────
  // Four hooks ran side by side whenever the game screen mounted, plus MainMenuPanel's auto-host and its redirect,
  // each watching the store for its moment. They are one entry now: identity and the stored game (side by side, as
  // before), then, if no seat was taken, the code, the cold rejoin, the auto-host, or back to the welcome screen.
  // Where the order is new it is flagged: the stored game and a table code used to RACE, and the code was dropped
  // when the stored game won; the stored game now goes first, and the code runs only if it did not seat the player.

  /**
   * The page loaded (authStore's `hydrate` and AuthRouter's URL bootstrap). The account is the one a stored,
   * still-valid token belongs to (FE3: `ClientAuthServer.account`, which read and checked it — hydrate's JWT half),
   * or the stored guest, or nobody; a token makes a stored guest forgotten, as hydrate did. Where the page lands:
   * Stripe's return, a `?join=` code (the game screen, with or without identity), a stored game for a known player
   * (the game screen, where it resumes), or the welcome screen. The "restoring" overlay is up while a game is stored,
   * as the game store's initial state had it.
   */
  sessionStarted(command: ICommandSessionStarted): void {
    const { intent, stored, token } = command;
    const credits = this.account.matchCredits;
    if (token !== null) {
      this.session.account = Account.registered(token.playerId, null, credits).toSnapshot();
      if (stored.guest !== null) this.keepGuest(null);
    } else {
      this.session.account = (stored.guest !== null
        ? Account.guest(stored.guest.playerId, stored.guest.displayName, credits)
        : Account.anonymous(credits)).toSnapshot();
    }
    const hasIdentity = token !== null || stored.guest !== null;
    const joinCode = (intent.joinCode ?? '').trim().toUpperCase();
    this.session.restoring = stored.resume !== null;
    if (intent.paymentReturn) {
      this.session.screen = 'PAYMENT_SUCCESS';
    } else if (joinCode !== '') {
      this.session.pendingRoomCode = joinCode;
      this.session.pendingJoinError = null;
      this.session.screen = 'GAME';
    } else {
      this.session.screen = hasIdentity && stored.resume !== null ? 'GAME' : 'WELCOME';
    }
  }

  /**
   * The game screen mounted. Refused while an entry is in flight — StrictMode mounts twice — or when the tab already
   * sits at a table. Identity: a known player, a stored guest seated now (free tier, no credits), the server's `me`
   * for a token, or a name to ask for. The stored game: nothing (the overlay drops), another player's record, trusted
   * as it is without asking the server (it belongs to an identity that has not hydrated yet — clearing it would lose
   * the game), or this player's, checked by its code.
   */
  planEntry(stored: IEntityClientStored): TDecisionResult<IEntityEntryPlan> {
    if (this.session.entering) return refuse('The game screen is already being entered.', REFUSAL.ALREADY);
    if (this.table.isSeated()) return refuse('Already at a table.', REFUSAL.ALREADY);
    this.session.entering = true;

    const account = this.account;
    let identity: IEntityEntryPlan['identity'] = 'name';
    if (this.session.player !== null) identity = 'known';
    else if (account.isRegistered()) identity = 'me';
    else {
      const guest = account.isGuest() ? account.returningGuest() : null;
      if (guest !== null) {
        this.seatAs(guest);
        identity = 'known';
      }
    }

    const record = stored.resume;
    let resume: IEntityEntryPlan['resume'] = 'validate';
    if (record === null) {
      resume = 'none';
      this.session.restoring = false;
    } else if (record.playerId !== account.playerId) {
      resume = 'trust';
    }
    const plan: IEntityEntryPlan = { identity, resume, record };
    return allowed(plan);
  }

  /**
   * `me` answered; true when the tab is to sign out. A player behind the token is seated, with their credits; one
   * already seated is left alone (the answer is late). No player: the token is worthless and the tab signs out —
   * after the expiry message, if a join was waiting on it, so the sign-out's reset leaves it showing.
   */
  meAnswered(me: IPayloadMe | null): boolean {
    if (me === null) {
      if (this.session.pendingRoomCode) this.session.pendingJoinError = SESSION_EXPIRED;
      return true;
    }
    if (this.session.player !== null) return false;
    const account = this.account;
    const player: IEntityClientPlayer = { playerId: me.id, displayName: me.displayName, matchCredits: me.matchCredits, isAdmin: me.isAdmin };
    this.seatAs(player);
    this.session.account = account.withCredits(me.matchCredits).toSnapshot();
    return false;
  }

  /**
   * `me` could not be asked — the server unreachable, or it failed (FE3; until then this read as "no player" and
   * signed the player out). The account stays signed in; the entry stops, and the welcome screen says why, so Play
   * enters again and asks again. Unless the stored game seated the tab meanwhile: then it plays (false: nothing done).
   */
  meUnreachable(): boolean {
    if (this.table.isSeated()) return false;
    this.session.screen = 'WELCOME';
    this.session.pendingJoinError = UNREACHABLE;
    return true;
  }

  /** The stored game's room, by its code: gone, unreachable or finished — forget it; otherwise go on (true). */
  resumeValidated(lookup: { readonly roomStatus: string } | null): boolean {
    if (lookup !== null && lookup.roomStatus !== ROOM_FINISHED) return true;
    this.session.restoring = false;
    this.keepStoredGame(null);
    return false;
  }

  /** Back into a stored game (`resumeIntoGame`): the overlay goes up, the record is kept, the room is re-joined. */
  planRestore(record: IEntityClientResume): TDecisionResult<IPayloadSeat> {
    this.session.restoring = true;
    this.keepStoredGame(record);
    const request: IPayloadSeat = { roomId: record.roomId, playerId: record.playerId };
    return allowed(request);
  }

  /**
   * The stored game's room answered the join, and the round to read next, if any. None: the game is over for this
   * player — forget it. A table that has not started: the lobby. One in progress: read its round next, still
   * restoring. The seat is the record's player, whoever the session has caught up to.
   */
  restoreJoined(command: ICommandRestoreJoined): IEntityRoundRead['read'] {
    const { record, room } = command;
    if (room === null) {
      this.session.restoring = false;
      this.keepStoredGame(null);
      return null;
    }
    this.seatedAt(TableRoom.restored(room, record));
    if (room.status !== ROOM_IN_PROGRESS) {
      this.table.enterLobby();
      this.session.restoring = false;
      return null;
    }
    return { roomId: room.id, playerId: record.playerId };
  }

  /**
   * The live round, after a seat was taken mid-game — a restore, a rejoin, a late join: the table's mapping
   * (`ClientTable.restoreRound`), as the request's player — the restore runs before `me` may have answered — and by
   * the ranking the session holds. False (ignored) when the tab no longer sits at that room.
   */
  roundRestored(command: ICommandRoundRestored): boolean {
    const { request, activeRound } = command;
    if (!this.table.sitsAt(request.roomId)) return false;
    this.table.restoreRound(activeRound, request.playerId, DiceRanking.of(this.session.diceRanking));
    this.session.restoring = false;
    this.session.pendingRoomCode = null;
    return true;
  }

  /**
   * Identity and the stored game have settled: what next. Seated: nothing to do. No player yet: wait for the name.
   * Then, as the hooks' guards had it: a pending code joins (and rules out the cold rejoin); a registered player with
   * nothing stored asks the server for a live room; the funnel's host intent creates a table; and with nothing left,
   * MainMenuPanel's redirect — back to the welcome screen.
   */
  planIntent(coldChecked: boolean): IEntityIntent['next'] {
    if (this.table.isSeated()) return 'seated';
    if (this.session.player === null) return 'wait';
    if (this.session.pendingRoomCode) return 'join';
    if (!coldChecked && this.account.isRegistered()) return 'cold';
    if (this.session.hostOnEntry) return 'host';
    this.session.screen = 'WELCOME';
    return 'idle';
  }

  /** The entry is over, however it ended; the next mount may enter again. */
  entryFinished(): void {
    this.session.entering = false;
  }

  // ── The entry funnel (FE2): IdentityModal, EnterCodeScreen, the funnel's screen changes ──────────────────────

  /** Where a funnel step leads: a player with an identity skips the guest/login choice. */
  private onwardScreen(): TClientScreen {
    return this.account.isAnonymous() ? 'GUEST_CHOICE' : 'GAME';
  }

  /** A guest's name, checked before anything is created. */
  planGuest(displayName: string): TDecisionResult<IPayloadGuest> {
    const trimmed = displayName.trim();
    if (trimmed.length === 0) return refuse('Please enter a name.', REFUSAL.INVALID_NAME);
    if (trimmed.length > MAX_NAME_LENGTH) return refuse(`Name must be ${MAX_NAME_LENGTH} characters or fewer.`, REFUSAL.INVALID_NAME);
    const request: IPayloadGuest = { displayName: trimmed };
    return allowed(request);
  }

  /**
   * The server created the player: the tab is seated as them. Without a registered account the tab is that guest
   * from now on, kept across a reload; a registered account keeps its own identity.
   */
  guestCreated(player: ICommandGuestCreated['player']): void {
    const account = this.account;
    const seat: IEntityClientPlayer = { playerId: player.id, displayName: player.displayName, matchCredits: player.matchCredits, isAdmin: false };
    this.seatAs(seat);
    if (account.isRegistered()) return;
    this.session.account = Account.guest(player.id, player.displayName, account.matchCredits).toSnapshot();
    const guest: IEntityClientGuest = { playerId: player.id, displayName: player.displayName };
    this.keepGuest(guest);
  }

  /**
   * EnterCodeScreen's Continue. No code: host a new table — straight to the game for a known player, to the guest
   * choice otherwise. A code: look it up first.
   */
  planTableCode(code: string): TDecisionResult<IPayloadCodeLookup> {
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length > 0) {
      const lookup: IPayloadCodeLookup = { code: trimmed };
      return allowed(lookup);
    }
    this.session.pendingRoomCode = null;
    this.session.hostOnEntry = true;
    this.session.screen = this.onwardScreen();
    const none: IPayloadCodeLookup = { code: null };
    return allowed(none);
  }

  /** The code's lookup: no such table is refused with the screen's message; a table is joined, not hosted. */
  tableCodeChecked(code: string, found: boolean): TDecisionResult<IEntityTableAck> {
    if (!found) return refuse('No game table found for that code. Check it and try again.', REFUSAL.NOT_FOUND);
    this.session.hostOnEntry = false;
    this.session.pendingRoomCode = code;
    this.session.pendingJoinError = null;
    this.session.screen = this.onwardScreen();
    return allowed(APPLIED);
  }

  /**
   * A funnel button's screen change. Leaving the game screen also ends a failed auto-host's panel — "Choose
   * manually" is this — so a later visit does not show the old error.
   */
  navigate(screen: TClientScreen): void {
    this.session.screen = screen;
    if (screen !== 'GAME') {
      this.session.hosting = false;
      this.session.hostError = null;
    }
  }

  /** The welcome screen's ✕ on a join error. */
  dismissJoinError(): void {
    this.session.pendingJoinError = null;
  }

  // ── Taking a seat (FE2): useSmartJoinRouting, MainMenuPanel's auto-host, useColdLoginRejoin ──────────────────
  // The messages are theirs, word for word: the welcome screen shows them. Every failure drops the pending code,
  // which is what stopped the routing effect re-firing on the same code forever; here it stops the entry asking again.

  /** A pending code, for a known player: look it up, upper-cased as the routing hook did. */
  planJoin(): TDecisionResult<IPayloadCodeLookup> {
    const code = this.session.pendingRoomCode;
    if (!code || this.session.player === null) return refuse('No table code to join, or no player yet.', REFUSAL.NOT_READY);
    const request: IPayloadCodeLookup = { code: code.trim().toUpperCase() };
    return allowed(request);
  }

  /** The join stopped here: the welcome screen says why. */
  private stopped(message: string): TDecisionResult<IEntityJoinRoute> {
    this.joinFailed(message);
    const stop: IEntityJoinRoute = { route: { type: 'stop' } };
    return allowed(stop);
  }

  /**
   * WHICH WAY A TABLE CODE LEADS THIS PLAYER — SmartJoinByCodeUseCase's rule (`smartJoinRoute`, moved to the Domain in
   * FE2 as `game/SmartJoinRoute.ts`; a method of the session since P4, which knows who is asking). A waiting room is
   * joined. A room in progress is re-joined when one of its seats is this player's (`returnsTo`), late-joined under a
   * name made unique (`lateJoinName`), or full at six. Anything else — no such room, a finished one, a lookup that
   * failed — stops with the welcome screen's message. A re-join sends this tab's own identity, which the server
   * matches; the seat it matched is not sent.
   */
  joinRouted(command: ICommandJoinRouted): TDecisionResult<IEntityJoinRoute> {
    const player = this.session.player;
    if (player === null) return refuse('No player to seat.', REFUSAL.NOT_READY);
    if (command.failed) return this.stopped(UNREACHABLE);
    const lookup = command.lookup;
    if (lookup === null) return this.stopped(TABLE_NOT_FOUND);
    if (lookup.roomStatus === ROOM_WAITING) {
      const request: IPayloadSeat = { roomId: lookup.roomId, playerId: player.playerId };
      const join: IEntityJoinRoute = { route: { type: 'JOIN', request } };
      return allowed(join);
    }
    if (lookup.roomStatus !== ROOM_IN_PROGRESS) return this.stopped(TABLE_NOT_FOUND);
    if (this.returnsTo(lookup, player)) {
      const request: IPayloadRejoinRequest = {
        roomCode: command.code, playerId: player.playerId, displayName: player.displayName, email: this.account.email,
      };
      const rejoin: IEntityJoinRoute = { route: { type: 'REJOIN', request } };
      return allowed(rejoin);
    }
    if (lookup.playerCount >= MAX_PLAYERS) return this.stopped(TABLE_FULL);
    const request: IPayloadLateJoin = {
      roomId: lookup.roomId, playerId: player.playerId, displayName: ClientSession.lateJoinName(lookup, player.displayName), matchCredits: player.matchCredits,
    };
    const lateJoin: IEntityJoinRoute = { route: { type: 'LATE_JOIN', request } };
    return allowed(lateJoin);
  }

  /**
   * One of a room in progress's seats is this player's. A returning player must be able to rejoin even if the backend
   * still treats their (churning) connection as live — so match against ALL members, not just disconnected slots (a
   * still-"connected" member who landed back at WELCOME otherwise fell through to LATE_JOIN, which throws "already in
   * the room"). Id and email identify the SAME person reliably, so they match any member; a bare display-name match
   * is only trusted for a *suspended* slot (the guest re-identification case) to avoid colliding with a different
   * connected player of the same name. The backend resumes the same player and rejects a different person who
   * matches a still-connected member (PLAYER_ALREADY_CONNECTED).
   */
  private returnsTo(lookup: IPayloadRoomCodeLookup, player: IEntityClientPlayer): boolean {
    const members = lookup.suspendedPlayers;
    if (members.some((p) => p.id === player.playerId)) return true;
    const email = this.account.email;
    if (email && members.some((p) => p.email?.toLowerCase() === email.toLowerCase())) return true;
    return members.some((p) => !p.isConnected && p.displayName.toLowerCase() === player.displayName.toLowerCase());
  }

  /**
   * A genuinely different player joining mid-game (an id or email match is a re-join). A nickname already at the
   * table gets '*' appended, repeatedly, so it is told apart — they still join, shown as "Bob*" or "Bob**" rather
   * than colliding with the "Bob" already there.
   */
  private static lateJoinName(lookup: IPayloadRoomCodeLookup, displayName: string): string {
    const taken = new Set(lookup.suspendedPlayers.map((p) => p.displayName.toLowerCase()));
    let name = displayName;
    while (taken.has(name.toLowerCase())) name += '*';
    return name;
  }

  /** A lobby joined: seated at full lives, in the lobby; every seat the server lists gets its lives. True: go on. */
  tableJoined(command: ICommandTableJoined): boolean {
    const { request, room } = command;
    if (room === null) {
      this.joinFailed(JOIN_FAILED);
      return false;
    }
    this.seatedAt(TableRoom.joined(room, request.playerId));
    this.table.livesFor(room.playerIds);
    this.table.enterLobby();
    return true;
  }

  /**
   * A seat re-taken by code. The server matched it by email or name and answers with ITS player id, which for a
   * guest differs from this fresh tab's: the tab adopts it, or it is never recognised as that player and a stale
   * "new player" seat appears beside the resumed one. A guest's stored identity follows. Then the live round is read,
   * so the player lands on the real phase.
   */
  tableRejoined(command: ICommandTableRejoined): TDecisionResult<IEntityRoundRead> {
    const player = this.session.player;
    if (player === null) return refuse('No player to seat.', REFUSAL.NOT_READY);
    const { rejoin } = command;
    if (rejoin === null) {
      this.joinFailed(REJOIN_FAILED);
      return allowed(NO_READ);
    }
    const effective = rejoin.playerId;
    const account = this.account;
    if (effective !== player.playerId) {
      const adopted: IEntityClientPlayer = { ...player, playerId: effective };
      this.seatAs(adopted);
      if (account.isGuest()) {
        this.session.account = account.adoptedAs(effective, player.displayName).toSnapshot();
        const guest: IEntityClientGuest = { playerId: effective, displayName: player.displayName };
        this.keepGuest(guest);
      }
    }
    const source: IEntityRoomSource = { id: rejoin.roomId, roomCode: rejoin.roomCode, players: rejoin.players, status: rejoin.roomStatus };
    this.seatedAt(TableRoom.joined(source, effective));
    const read: IEntityRoundRead = { read: { roomId: rejoin.roomId, playerId: effective } };
    return allowed(read);
  }

  /** A game in progress joined late: seated, then the live round is read. */
  tableLateJoined(command: ICommandTableLateJoined): IEntityRoundRead['read'] {
    const { request, room } = command;
    if (room === null) {
      this.joinFailed(JOIN_FAILED);
      return null;
    }
    this.seatedAt(TableRoom.joined(room, request.playerId));
    return { roomId: request.roomId, playerId: request.playerId };
  }

  /** The funnel asked to host: create the table, once — the intent is spent as the creation starts. */
  planHost(): TDecisionResult<IPayloadHost> {
    const player = this.session.player;
    if (!this.session.hostOnEntry || player === null) return refuse('Nothing to host, or no player yet.', REFUSAL.NOT_READY);
    this.session.hostOnEntry = false;
    this.session.hosting = true;
    this.session.hostError = null;
    const request: IPayloadHost = { hostPlayerId: player.playerId };
    return allowed(request);
  }

  /**
   * The new table; true to go on. Created: the host sits in the lobby. Not created: the panel keeps showing, with the
   * server's own message, or "Unexpected error." when the failure was not the server's answer.
   */
  tableHosted(command: ICommandTableHosted): boolean {
    const { request, room, serverMessage } = command;
    if (room === null) {
      this.session.hostError = serverMessage ?? UNEXPECTED;
      return false;
    }
    this.seatedAt(TableRoom.hosted(room, request.hostPlayerId));
    this.table.enterLobby();
    this.session.hosting = false;
    this.session.hostError = null;
    return true;
  }

  /**
   * A registered player came back with nothing stored — the browser was closed — so the server is asked whether they
   * still have a live room. The overlay is up meanwhile.
   */
  planColdRejoin(): TDecisionResult<IPayloadPlayer> {
    const player = this.session.player;
    if (player === null) return refuse('No player to look up.', REFUSAL.NOT_READY);
    this.session.restoring = true;
    const request: IPayloadPlayer = { playerId: player.playerId };
    return allowed(request);
  }

  /**
   * The server's answer: the game to resume, if any. No live room (or no answer): the overlay drops. A live room:
   * resumed straight in, no prompt — the server lists only a non-finished room this player is a member of.
   */
  activeRoomFound(command: ICommandActiveRoomFound): IEntityColdRoom['record'] {
    const { request, room } = command;
    if (room !== null) return { playerId: request.playerId, roomId: room.roomId, roomCode: room.roomCode };
    this.session.restoring = false;
    return null;
  }

  // ── The rules the table is played by (frontend-domain-oo P2) ──────────────────────────────────────────────────

  /**
   * THE RULES ARRIVED: the server's ranking is kept in the session, which a table's reset does not touch, and the
   * table's derived values are read again by it. A ranking that holds no combination is no ranking, and the one held
   * stays; so it does when the same one arrives again — every game screen asks, Infrastructure asks the server once.
   * True when kept.
   */
  rankingLoaded(ranking: ReadonlyArray<IEntityClientDiceRank>): boolean {
    if (ranking.length === 0 || sameJson(ranking, this.session.diceRanking)) return false;
    this.session.diceRanking = [...ranking];
    return true;
  }
}
